import { useState } from 'react'
import { useApp } from '@/store/useApp'
import { SCHOOL_NAMES, type CalcResult, type SchoolName } from '@/domain/types'
import { track } from '@/lib/track'
import { isOverloadStatus, redirectToOverload } from '@/lib/overload'
import { buildPredictionSnapshot } from '@/lib/snapshot'

type SubmitState = 'idle' | 'submitting' | 'success' | 'error'

type AdmittedValue = SchoolName | '未录取'

/** 录取通道：仅 normal（统招/普通生）进入偏好矩阵校准主集，其余通道有独立录取规则 */
const ADMISSION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'normal', label: '统招（普通生）' },
  { value: 'indicator', label: '指标生' },
  { value: 'recommend', label: '推荐生' },
  { value: 'art_sports', label: '艺体特长生' },
  { value: 'vocational', label: '职普融通班' },
  { value: 'other', label: '其他 / 不确定' }
]

/** 提交来源页（运营归因）。FeedbackSection 目前主要出现在结果区。 */
function detectSource(override?: string): string {
  if (override) return override
  if (typeof window === 'undefined') return 'other'
  const p = window.location.pathname
  if (p === '/' || p === '') return 'result'
  if (p.startsWith('/report')) return 'report'
  if (p.startsWith('/share')) return 'share'
  if (p.startsWith('/article')) return 'article'
  return 'other'
}

export function FeedbackSection({ result, source }: { result: CalcResult; source?: string }) {
  const { visitorId } = useApp()
  // 分数 / 年份以「当前展示的这份预测」为准（result.input），而非全局 store：
  // 报告页 / 分享页可能由分享链接直达，此时 store.score 为空，但 result 始终有效。
  const score = result.input.score
  const year = result.input.policy.year
  const [first, setFirst] = useState<SchoolName | ''>('')
  const [admitted, setAdmitted] = useState<AdmittedValue | ''>('')
  const [second, setSecond] = useState<SchoolName | ''>('')
  const [admissionType, setAdmissionType] = useState<string>('normal')
  const [showMore, setShowMore] = useState(false)
  const [state, setState] = useState<SubmitState>('idle')
  const [errMsg, setErrMsg] = useState<string>('')

  async function onSubmit() {
    if (!first || !admitted) return
    setState('submitting')
    try {
      const res = await fetch('/api/h5/v1/zhcz/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId,
          year,
          score,
          first_choice: first,
          admitted,
          // 选填增强字段：后端会对非法值降级为 null，不阻断回填
          second_choice: second || undefined,
          admission_type: admissionType,
          source: detectSource(source),
          prediction_snapshot: buildPredictionSnapshot(result)
        })
      })
      if (isOverloadStatus(res.status)) {
        redirectToOverload()
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setState('success')
      track('feedback_submit', {
        year,
        first_choice: first,
        admitted,
        admission_type: admissionType,
        hit: first === admitted ? 'self' : String(admitted) === '未录取' ? 'none' : 'other'
      })
    } catch (e: unknown) {
      setState('error')
      setErrMsg((e as Error)?.message || '提交失败，请稍后再试。')
    }
  }

  if (!result.position.band && !result.position.inTop300) return null

  if (state === 'success') {
    return (
      <section className="card bg-green-50 border border-green-100">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🎉</span>
          <div>
            <div className="text-green-800 font-medium mb-1">感谢回填，已帮助校准明年预测！</div>
            <p className="text-green-700 text-sm leading-relaxed">
              我们已记录你这次的分数、志愿与真实录取结果（以及当时模型给你的预测），
              明年会用它把学弟学妹的录取概率校得更准。欢迎关注服务号「卓越方法星球」，
              明年第一时间获取新版测算。
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="card">
      <h2 className="section-title">回填录取结果</h2>
      <p className="text-ink-600 text-sm mb-5 leading-relaxed">
        中考录取结束后，欢迎你回来告诉我们最终录取结果。你的数据将用于校准明年的模型 ——
        我们不收集任何身份信息。
      </p>

      <div className="space-y-5">
        {/* 第一志愿选择 */}
        <div>
          <label className="block text-ink-700 text-sm font-medium mb-2">当年第一志愿</label>
          <SchoolPicker
            options={SCHOOL_NAMES.map((s) => ({ value: s, label: s }))}
            value={first}
            onChange={(v) => setFirst(v as SchoolName)}
          />
        </div>

        {/* 实际录取选择 */}
        <div>
          <label className="block text-ink-700 text-sm font-medium mb-2">实际录取学校</label>
          <SchoolPicker
            options={[
              ...SCHOOL_NAMES.map((s) => ({ value: s, label: s })),
              { value: '未录取', label: '未被普通高中录取' }
            ]}
            value={admitted}
            onChange={(v) => setAdmitted(v as AdmittedValue)}
          />
        </div>

        {/* 补充信息（选填）—— 折叠，保持主流程两步内完成 */}
        <div className="border-t border-ink-100 pt-4">
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="flex items-center gap-1.5 text-ink-600 text-sm font-medium"
            aria-expanded={showMore}
          >
            <span>补充信息（选填，让明年预测更准）</span>
            <span className={'transition-transform ' + (showMore ? 'rotate-180' : '')} aria-hidden="true">
              ⌄
            </span>
          </button>

          {showMore && (
            <div className="space-y-5 mt-4">
              {/* 录取通道 */}
              <div>
                <label className="block text-ink-700 text-sm font-medium mb-2">录取通道</label>
                <p className="text-ink-400 text-xs mb-2 leading-relaxed">
                  仅「统招（普通生）」用于校准录取概率模型；指标生 / 推荐生等有独立规则。
                </p>
                <SchoolPicker
                  options={ADMISSION_TYPES}
                  value={admissionType}
                  onChange={(v) => setAdmissionType(v)}
                />
              </div>

              {/* 第二志愿 */}
              <div>
                <label className="block text-ink-700 text-sm font-medium mb-2">当年第二志愿（如有）</label>
                <SchoolPicker
                  options={[
                    { value: '', label: '未填 / 不记得' },
                    ...SCHOOL_NAMES.map((s) => ({ value: s, label: s }))
                  ]}
                  value={second}
                  onChange={(v) => setSecond(v as SchoolName | '')}
                />
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onSubmit}
          disabled={!first || !admitted || state === 'submitting'}
          className="btn-primary w-full disabled:opacity-50 disabled:active:scale-100"
        >
          {state === 'submitting' ? '提交中…' : '提交回填'}
        </button>

        {state === 'error' && (
          <div className="bg-rose-50 text-rose-800 text-sm px-3 py-2.5 rounded-lg">{errMsg}</div>
        )}
      </div>
    </section>
  )
}

/** 卡片式单选组件，替代原生 select */
function SchoolPicker({
  options,
  value,
  onChange
}: {
  options: Array<{ value: string; label: string }>
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => (
        <button
          key={opt.value || '__empty__'}
          type="button"
          onClick={() => onChange(opt.value)}
          className={
            'px-3 py-2.5 rounded-xl border-2 text-sm text-left transition active:scale-[0.98] ' +
            (value === opt.value
              ? 'border-brand-500 bg-brand-50 text-brand-900 font-medium'
              : 'border-ink-200 bg-white text-ink-700')
          }
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
